// /ai — the satirical "JoshLM-7B" portfolio agent.
//
// Front-of-house: claims to be a 7-billion-parameter model fine-tuned on
// harsh.joshi.pth's life. Subsecond. State-of-the-art on the Joshi-Bench.
//
// Back-of-house: BM25 + 22MB all-MiniLM-L6-v2 embedding model running
// entirely in your browser. Hybrid retrieval, Reciprocal Rank Fusion,
// rule-based intent classifier, template composer. ~50 lines of actual
// agent loop. The marketing is the heaviest layer.
//
// Easter eggs (layered):
//   1. Console log on page mount — the truth, with a wink
//   2. Tiny ? button in the trace panel → modal explaining the real arch
//   3. Specific prompts ("what model are you") → satirical reveal

import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowUp, FileText, Github, Mail, BookOpen, Mic, MicOff,
  Brain, Sparkles, HelpCircle, X, Cpu, Zap, Volume2, VolumeX,
} from "lucide-react";
import { retrieve } from "@/lib/retrieval";
import { compose, type Intent } from "@/lib/composer";
import {
  checkGroundedness,
  retrievalGate,
  type GroundednessReport,
} from "@/lib/groundedness";
import { preloadEmbedder, getEmbedderState, onEmbedderStateChange, type EmbedderState } from "@/lib/embedder";
import { preloadCorpusEmbeddings } from "@/lib/retrieval";
import { useVoiceInput } from "@/lib/useVoiceInput";
import { useGenerativeLLM, QWEN_MODEL_ID } from "@/lib/genllm";
import { detectCapability, type CapabilityReport } from "@/lib/capability";
import { routeQuery } from "@/lib/toolRouter";
import { mcpCallMany, type MCPCallResult } from "@/lib/mcpTransport";
import { useTTS } from "@/lib/tts";
import { runAgentLoop, type AgentMessage, type ActionChoice } from "@/lib/agentLoop";
import { runProAgent } from "@/lib/proAgentClient";
import { FULL_CORPUS } from "@/data/corpus";
import { toast } from "sonner";
import { Rocket } from "lucide-react";
import { EvalsPill, EvalsSheet } from "@/components/ai/EvalsSheet";
import { computeTrustVerdict, type TrustVerdict } from "@/lib/trustAudit";
import { TraceScrubber } from "@/components/ai/TraceScrubber";

type Message = { role: "user" | "assistant"; content: string };

type AgentAction = {
  id: string;
  name:
    | "bm25Search"
    | "embeddingRerank"
    | "composeResponse"
    | "generateResponse"
    | "groundednessCheck"
    | "mcpToolCall"
    | "reflectionRetry"
    | "agentThought"
    | "agentAction"
    | "agentObservation";
  status: "running" | "ok" | "failed";
  durationMs?: number;
  detail?: Record<string, unknown>;
};

const SUGGESTED = [
  "What's ReallyArtificial.org?",
  "What did he do as a forward-deployed engineer?",
  "Why should OpenAI hire Harsh?",
  "What is Harsh working on now?",
  "What model are you?",
  "How do I contact him?",
];

export default function AI() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamBuf, setStreamBuf] = useState("");
  const [actions, setActions] = useState<AgentAction[]>([]);
  const [embedderState, setEmbedderStateLocal] = useState<EmbedderState>(getEmbedderState());
  const [truthOpen, setTruthOpen] = useState(false);
  const [capability, setCapability] = useState<CapabilityReport | null>(null);
  const [smartMode, setSmartMode] = useState(false);
  const [proMode, setProMode] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("joshlm-pro-mode") === "1";
  });
  const [evalsOpen, setEvalsOpen] = useState(false);
  // Trust verdict per assistant message, keyed by its index in `messages`.
  // Populated after Pro Mode responses complete; surfaced inline under
  // the answer so visitors can see grounding without opening the trace.
  const [messageTrust, setMessageTrust] = useState<Record<number, TrustVerdict>>({});
  const scrollRef = useRef<HTMLDivElement>(null);

  // Persist Pro Mode preference. Surface a toast on enable so visitors
  // understand that the call leaves their browser and hits a 70B model
  // on Groq via Vercel — the satire is suspended in this mode.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("joshlm-pro-mode", proMode ? "1" : "0");
  }, [proMode]);

  const voice = useVoiceInput({ onTranscript: (t) => setInput(t) });
  const llm = useGenerativeLLM();
  const tts = useTTS();

  // Auto-load policy: once we know the device is capable, kick off the
  // model download in the background. User can still toggle it off.
  useEffect(() => {
    const report = detectCapability();
    setCapability(report);
    if (report.decision === "auto-load") {
      setSmartMode(true);
      void llm.load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If user toggles Smart Mode on later, trigger the load
  useEffect(() => {
    if (smartMode && llm.state.status === "idle") void llm.load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smartMode]);

  // ── Page-mount setup ─────────────────────────────────────────
  useEffect(() => {
    // Easter egg #1: console reveal
    // eslint-disable-next-line no-console
    console.log(
      "%cJoshLM-7B?",
      "color:#0a0a0a;font-weight:700;font-size:14px",
    );
    // eslint-disable-next-line no-console
    console.log(
      "%cpsst… this isn't an LLM. it's BM25 + a 22MB MiniLM embedding model running entirely in your browser. the whole thing fits in less RAM than ChatGPT's favicon. welcome to \"AI\" in 2026. — Harsh",
      "color:#666;font-style:italic;font-size:12px;line-height:1.5",
    );

    // Start loading the model so it's warm by the time the user types
    preloadEmbedder();
    preloadCorpusEmbeddings();
  }, []);

  useEffect(() => {
    const off = onEmbedderStateChange((s) => setEmbedderStateLocal(s));
    return () => {
      off();
    };
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, streamBuf, actions]);

  // ── Submit handler ───────────────────────────────────────────
  const send = useCallback(async (text: string) => {
    const q = text.trim();
    if (!q || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q }]);
    setStreaming(true);
    setStreamBuf("");

    // Action: bm25Search
    const bm25Id = `a_${Date.now()}_bm25`;
    setActions((a) => [...a, { id: bm25Id, name: "bm25Search", status: "running" }]);
    const t0 = performance.now();
    const result = await retrieve(q, 5);
    const totalMs = Math.round(performance.now() - t0);

    setActions((a) =>
      a.map((act) =>
        act.id === bm25Id
          ? {
              ...act,
              status: "ok",
              durationMs: Math.round(result.trace.timings.bm25Ms),
              detail: {
                topHits: result.trace.bm25Hits.slice(0, 5),
                totalCandidates: result.trace.bm25Hits.length,
              },
            }
          : act,
      ),
    );

    if (result.trace.embedderUsed) {
      const semId = `a_${Date.now()}_sem`;
      setActions((a) => [
        ...a,
        {
          id: semId,
          name: "embeddingRerank",
          status: "ok",
          durationMs: Math.round(result.trace.timings.embedMs + result.trace.timings.semanticMs),
          detail: {
            topHits: result.trace.semanticHits.slice(0, 5),
            embedMs: result.trace.timings.embedMs,
            cosineMs: result.trace.timings.semanticMs,
          },
        },
      ]);
    }

    // Action: composeResponse
    const composeId = `a_${Date.now()}_compose`;
    setActions((a) => [
      ...a,
      { id: composeId, name: "composeResponse", status: "running" },
    ]);
    const topScore = result.trace.finalHits[0]?.rrfScore ?? 0;
    const composed = compose(q, result.chunks, topScore);
    setActions((a) =>
      a.map((act) =>
        act.id === composeId
          ? {
              ...act,
              status: "ok",
              durationMs: Math.max(1, Math.round(totalMs - result.trace.timings.totalMs)),
              detail: {
                intent: composed.intent as Intent,
                citedChunkIds: composed.citedChunkIds,
                satireMode: composed.satireMode,
              },
            }
          : act,
      ),
    );

    // ── Guardrail layer 1: retrieval-confidence gate (TOPIC-AGNOSTIC).
    //    If nothing in the corpus is even relevant to the question, refuse
    //    before invoking the LLM. Beats keyword denylists because it works
    //    for any off-corpus question — geography, opinions, made-up entities,
    //    relationships, anything. The gate uses BM25 + embedding scores.
    const topBM25 = result.trace.bm25Hits[0]?.score ?? 0;
    const topEmbCos = result.trace.semanticHits[0]?.score ?? 0;
    const gate = retrievalGate(topBM25, topEmbCos);
    const gateId = `a_${Date.now()}_gate`;
    setActions((a) => [
      ...a,
      {
        id: gateId,
        name: "groundednessCheck",
        status: gate.passed ? "ok" : "failed",
        durationMs: 0,
        detail: {
          layer: "retrieval-gate",
          ...gate,
        },
      },
    ]);
    if (!gate.passed) {
      const refusal =
        "I don't have anything in my knowledge base that's relevant to that question. The corpus here covers Harsh's professional work, projects, code, and writing. For anything else, email him at harsh.joshi.pth@gmail.com.";
      tts.stop();
      await fakeStream(refusal, (delta, acc) => {
        setStreamBuf(acc);
        tts.feed(delta);
      });
      tts.finish();
      setMessages((m) => [...m, { role: "assistant", content: refusal }]);
      setStreamBuf("");
      setStreaming(false);
      return;
    }

    // ── Pro Mode: route to Groq Llama 3.3 70B via Vercel AI SDK ───────
    // Production agent harness with native function calling on the server.
    // Browser still does retrieval (corpus context goes in the system
    // prompt) and TTS streaming. Falls back silently on 429 / 503.
    if (proMode && !composed.satireMode) {
      tts.stop();
      // Map our chat history into the role/content shape the API expects.
      const proHistory = messages.slice(-6).map((m) => ({
        role: m.role as "user" | "assistant",
        content: m.content,
      }));
      // Send only what the model needs to ground (top retrieved chunks).
      const corpusContext = result.chunks
        .slice(0, 12)
        .map((c) => ({ id: c.id, text: c.text }));
      // Trace card per active tool call, keyed by callId so we can flip
      // status on tool-output-available.
      const toolCardByCallId = new Map<string, string>();
      // Raw outputs the model received this turn — passed into the trust
      // audit so tool-returned values (e.g. live star counts, commit SHAs)
      // don't count as "unsupported entities".
      const toolOutputs: unknown[] = [];
      let streamedAnswer = "";
      let didFallback = false;

      await runProAgent(
        { query: q, history: proHistory, corpusContext },
        {
          onTextDelta: (delta) => {
            streamedAnswer += delta;
            setStreamBuf(streamedAnswer);
            tts.feed(delta);
          },
          onToolStart: (name, callId) => {
            const cardId = `pro_${Date.now()}_act_${callId.slice(0, 6)}`;
            toolCardByCallId.set(callId, cardId);
            setActions((a) => [
              ...a,
              {
                id: cardId,
                name: "agentAction",
                status: "running",
                detail: {
                  iteration: toolCardByCallId.size,
                  kind: "tool",
                  summary: `TOOL ${name}`,
                  toolName: name,
                  callId,
                  via: "groq/llama-3.3-70b",
                },
              },
            ]);
          },
          onToolInputEnd: (callId, fullJson) => {
            const cardId = toolCardByCallId.get(callId);
            if (!cardId) return;
            setActions((a) =>
              a.map((act) =>
                act.id === cardId
                  ? {
                      ...act,
                      detail: {
                        ...(act.detail ?? {}),
                        argsJson: fullJson,
                      },
                    }
                  : act,
              ),
            );
          },
          onToolOutput: (callId, output) => {
            toolOutputs.push(output);
            const cardId = toolCardByCallId.get(callId);
            if (cardId) {
              setActions((a) =>
                a.map((act) =>
                  act.id === cardId
                    ? { ...act, status: "ok" }
                    : act,
                ),
              );
            }
            const obsId = `pro_${Date.now()}_obs_${callId.slice(0, 6)}`;
            const errored =
              typeof output === "object" &&
              output !== null &&
              "error" in (output as Record<string, unknown>);
            setActions((a) => [
              ...a,
              {
                id: obsId,
                name: "agentObservation",
                status: errored ? "failed" : "ok",
                detail: {
                  // iteration + observation are read by summariseDetail
                  iteration: toolCardByCallId.size,
                  observation: summarisePoolOutput(output),
                  callId,
                  data: output,
                  via: "groq/llama-3.3-70b",
                },
              },
            ]);
          },
          onFinish: (finalText) => {
            // streamedAnswer already accumulated via onTextDelta.
            if (!streamedAnswer && finalText) {
              setStreamBuf(finalText);
              streamedAnswer = finalText;
            }
          },
          onError: (kind, message) => {
            didFallback = true;
            if (kind === "rate_limit") {
              toast.info("Groq free tier rate limit hit — falling back to JoshLM-7B.", {
                description: "Pro Mode resumes tomorrow when the daily quota resets.",
              });
            } else if (kind === "unconfigured") {
              toast.warning("Pro Mode unavailable — GROQ_API_KEY not configured.", {
                description: "Switching off Pro Mode for now.",
              });
              setProMode(false);
            } else {
              toast.error("Pro Mode hit an error — falling back to JoshLM-7B.", {
                description: message,
              });
            }
          },
        },
      );

      tts.finish();

      if (!didFallback) {
        const final = streamedAnswer.trim();
        if (final) {
          // Capture the message's index BEFORE the state update so the
          // trust verdict can attach to the right row when it resolves.
          const newIdx = messages.length + 1; // +1 because the user msg already pushed
          setMessages((m) => [...m, { role: "assistant", content: final }]);
          setStreamBuf("");
          setStreaming(false);
          // Background: compute trust verdict and stash by message index.
          // Failures are non-fatal — the UI just won't show a verdict for
          // this answer.
          void computeTrustVerdict({
            response: final,
            chunks: result.chunks,
            chunkEmbeddings: result.chunkEmbeddings,
            toolCalls: toolCardByCallId.size,
            toolOutputs,
          })
            .then((verdict) => {
              setMessageTrust((prev) => ({ ...prev, [newIdx]: verdict }));
            })
            .catch(() => {
              /* skip — non-fatal */
            });
          return;
        }
        // Empty stream — treat as soft failure, fall through to next path.
      }
      // didFallback or empty answer → continue into the existing chain below.
      setStreamBuf("");
    }

    // ── Branch: real ReAct agent loop (Smart Mode) or rule-based + Path A.
    const useAgent =
      smartMode && llm.state.status === "ready" && !composed.satireMode;

    if (useAgent) {
      // ── Build a thin LLMCaller adapter around llm.generate. The agent loop
      //    needs a non-streaming call that returns the full output for parsing.
      tts.stop();
      const llmCall = async (
        messages: AgentMessage[],
        callOpts: { maxTokens?: number; temperature?: number; onToken?: (s: string) => void } = {},
      ): Promise<string> => {
        let buf = "";
        await new Promise<void>((resolve) => {
          void llm.generate(
            messages,
            {
              onToken: (delta) => {
                buf += delta;
                callOpts.onToken?.(delta);
              },
              onFirstToken: () => {},
              onDone: () => resolve(),
              onError: () => resolve(),
            },
            {
              maxTokens: callOpts.maxTokens ?? 220,
              temperature: callOpts.temperature ?? 0.2,
            },
          );
        });
        return buf;
      };

      // Convert chat history into AgentMessages for conversation awareness.
      // Only include the last 6 user/assistant turns to keep context bounded.
      const recentHistory: AgentMessage[] = messages
        .slice(-6)
        .map((m) => ({
          role: m.role === "user" ? "user" : "assistant",
          content: m.content,
        }));

      const agentRun = await runAgentLoop({
        query: q,
        history: recentHistory,
        chunks: result.chunks,
        llmCall,
        callbacks: {
          onStepBegin: (iter) => {
            // No card yet — wait for thought to populate
          },
          onThought: (iter, thought) => {
            const id = `agent_${Date.now()}_thought_${iter}`;
            setActions((a) => [
              ...a,
              {
                id,
                name: "agentThought",
                status: "ok",
                detail: { iteration: iter, thought },
              },
            ]);
          },
          onAction: (iter, action) => {
            const id = `agent_${Date.now()}_action_${iter}`;
            const summary =
              action.kind === "tool"
                ? `TOOL ${action.name}(${Object.entries(action.args).map(([k, v]) => `${k}=${typeof v === "string" ? `"${v}"` : v}`).join(", ")})`
                : action.kind === "answer"
                  ? "ANSWER (final)"
                  : "no-op";
            setActions((a) => [
              ...a,
              {
                id,
                name: "agentAction",
                status: action.kind === "noop" ? "failed" : "ok",
                detail: { iteration: iter, kind: action.kind, summary, action },
              },
            ]);
          },
          onObservation: (iter, observation, mcpResult) => {
            const id = `agent_${Date.now()}_obs_${iter}`;
            setActions((a) => [
              ...a,
              {
                id,
                name: "agentObservation",
                status: mcpResult && !mcpResult.ok ? "failed" : "ok",
                durationMs: mcpResult?.durationMs,
                detail: {
                  iteration: iter,
                  observation,
                  jsonrpcRequest: mcpResult?.request,
                  jsonrpcResponse: mcpResult?.response,
                  data: mcpResult?.data,
                },
              },
            ]);
          },
          onAnswerStream: (text) => {
            // The agent loop returns the final answer as one string; stream
            // it to the UI + TTS for a coherent feel.
            setStreamBuf("");
            void (async () => {
              let acc = "";
              for (const ch of text) {
                acc += ch;
                setStreamBuf(acc);
                tts.feed(ch);
                // small jitter so it doesn't all dump at once
                if (acc.length % 4 === 0) await new Promise((r) => setTimeout(r, 5));
              }
              tts.finish();
            })();
          },
        },
      });
      // Wait for the streamed-to-UI text to flush
      await new Promise((r) => setTimeout(r, 50));

      // ── Grounding check on the agent's final answer
      let final = agentRun.answer.trim();
      const groundCheckId = `a_${Date.now()}_ground`;
      setActions((a) => [
        ...a,
        { id: groundCheckId, name: "groundednessCheck", status: "running" },
      ]);
      let report: GroundednessReport;
      try {
        report = await checkGroundedness({
          response: final,
          chunks: result.chunks,
          chunkEmbeddings: result.chunkEmbeddings,
          topBM25Score: topBM25,
          topEmbeddingCosine: topEmbCos,
        });
      } catch {
        report = { retrieval: gate, semantic: null, entity: null, overall: "passed" };
      }
      setActions((a) =>
        a.map((act) =>
          act.id === groundCheckId
            ? {
                ...act,
                status: report.overall === "passed" ? "ok" : "failed",
                detail: {
                  layer: "post-agent",
                  overall: report.overall,
                  semanticWeakestScore: report.semantic?.weakestScore,
                  semanticWeakestSentence: report.semantic?.weakestSentence,
                  entityMatchRate: report.entity?.matchRate,
                  entityUnsupported: report.entity?.unsupported,
                  stopReason: agentRun.stopReason,
                  iterations: agentRun.steps.length,
                },
              }
            : act,
        ),
      );

      // If grounding failed, fall back to Path A template.
      const responseText = report.overall !== "passed" ? composed.text : final;
      setMessages((m) => [...m, { role: "assistant", content: responseText }]);
      setStreamBuf("");
      setStreaming(false);
      return;
    }

    // ── Path A (no Smart Mode): rule-based router + template compose ──
    const planned = routeQuery(q, result.chunks);
    if (planned.length > 0) {
      const callRecords = planned.map((p) => ({
        id: `mcp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        plan: p,
      }));
      setActions((a) => [
        ...a,
        ...callRecords.map((cr) => ({
          id: cr.id,
          name: "mcpToolCall" as const,
          status: "running" as const,
          detail: {
            tool: cr.plan.name,
            args: cr.plan.args,
            reason: cr.plan.reason,
          },
        })),
      ]);
      const toolResults: MCPCallResult[] = await mcpCallMany(
        planned.map((p) => ({ name: p.name, args: p.args })),
      );
      setActions((a) =>
        a.map((act) => {
          const idx = callRecords.findIndex((cr) => cr.id === act.id);
          if (idx === -1) return act;
          const tr = toolResults[idx];
          return {
            ...act,
            status: tr.ok ? "ok" : "failed",
            durationMs: tr.durationMs,
            detail: {
              ...(act.detail ?? {}),
              jsonrpcRequest: tr.request,
              jsonrpcResponse: tr.response,
              data: tr.data,
              error: tr.error,
            },
          };
        }),
      );
    }

    tts.stop();
    await fakeStream(composed.text, (delta, acc) => {
      setStreamBuf(acc);
      tts.feed(delta);
    });
    tts.finish();
    setMessages((m) => [...m, { role: "assistant", content: composed.text }]);
    setStreamBuf("");
    setStreaming(false);
  }, [streaming, smartMode, proMode, llm, tts, messages]);

  const showHero = messages.length === 0 && !streaming;

  return (
    <div className="min-h-screen bg-[#fafaf7] text-stone-900 font-inter selection:bg-stone-900 selection:text-stone-50">
      {/* faint dot grid */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 opacity-[0.025]"
        style={{
          backgroundImage:
            "radial-gradient(circle, #000 0.6px, transparent 0.6px)",
          backgroundSize: "18px 18px",
        }}
      />

      <div className="relative mx-auto flex min-h-screen max-w-6xl flex-col px-6 pb-36 pt-8 md:pt-12">
        {/* ── top bar ────────────────────────────────────────── */}
        <header className="flex items-baseline justify-between gap-4 pb-6">
          <div>
            <Link
              to="/"
              className="text-[14px] font-semibold tracking-[0.18em] text-stone-900 uppercase transition hover:text-stone-600"
            >
              Harsh Joshi
            </Link>
            <p className="mt-1 text-[12.5px] text-stone-500">
              Engineering & dark arts · <span className="text-stone-700">josharsh.com/ai</span>
            </p>
          </div>
          <nav className="hidden gap-5 text-[12px] text-stone-500 sm:flex">
            <a href="/resume-openai-startups.pdf" target="_blank" className="inline-flex items-center gap-1 hover:text-stone-900">
              <FileText className="h-3.5 w-3.5" /> Resume
            </a>
            <a href="https://github.com/josharsh" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-stone-900">
              <Github className="h-3.5 w-3.5" /> GitHub
            </a>
            <a href="https://unengineered.substack.com" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-stone-900">
              <BookOpen className="h-3.5 w-3.5" /> Writing
            </a>
            <a href="mailto:harsh.joshi.pth@gmail.com" className="inline-flex items-center gap-1 hover:text-stone-900">
              <Mail className="h-3.5 w-3.5" /> Email
            </a>
          </nav>
        </header>

        {/* ── two-pane body ──────────────────────────────────── */}
        <div className="grid flex-1 grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
          {/* CHAT pane */}
          <div className="flex min-h-0 flex-col">
            {/* hero */}
            <AnimatePresence>
              {showHero && (
                <motion.section
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.4 }}
                  className="pt-4 pb-2"
                >
                  <div className="flex items-center gap-3">
                    <JoshLMBadge />
                    <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                      online · subsecond
                    </span>
                  </div>
                  <h2 className="font-lora mt-6 text-[32px] leading-tight tracking-tight text-stone-900 md:text-[40px]">
                    Powered by JoshLM-7B.
                  </h2>
                  <p className="mt-3 max-w-2xl text-[14.5px] leading-relaxed text-stone-600">
                    A 7-billion-parameter language model{" "}
                    <span className="italic">fine-tuned on harsh.joshi.pth's life</span>.
                    State-of-the-art on the Joshi-Bench (n=1). Subsecond latency,
                    zero hallucinations*, full transparency through the trace
                    panel on the right.
                  </p>
                  <p className="mt-1.5 text-[10.5px] text-stone-400">
                    *Definitions of "language model" may vary. See the{" "}
                    <button
                      type="button"
                      onClick={() => setTruthOpen(true)}
                      className="underline decoration-stone-400 underline-offset-2 hover:text-stone-900"
                    >
                      ? button
                    </button>{" "}
                    in the trace panel.
                  </p>

                  <div className="mt-6 flex flex-wrap gap-2">
                    {SUGGESTED.map((p) => (
                      <button
                        key={p}
                        onClick={() => send(p)}
                        className="rounded-full border border-stone-200 bg-white px-3.5 py-1.5 text-[12.5px] text-stone-700 transition hover:border-stone-900 hover:text-stone-900"
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                  <p className="mt-5 inline-flex items-center gap-1.5 text-[11px] text-stone-400">
                    <Sparkles className="h-3 w-3" />
                    Model loading status: <span className="ml-1 font-mono">{embedderState}</span>
                  </p>
                </motion.section>
              )}
            </AnimatePresence>

            {/* transcript */}
            <div ref={scrollRef} className="flex-1 space-y-5 overflow-y-auto pt-3 pr-2">
              {messages.map((m, i) => (
                <MessageRow key={i} message={m} trust={messageTrust[i]} />
              ))}
              {streaming && (
                <MessageRow
                  message={{ role: "assistant", content: streamBuf || "…" }}
                  streaming
                />
              )}
            </div>
          </div>

          {/* TRACE pane */}
          <div className="self-start lg:sticky lg:top-6 lg:max-h-[calc(100vh-8rem)]">
            <div className="overflow-hidden rounded-xl border border-stone-200 bg-white/70 backdrop-blur">
              <div className="flex items-center justify-between border-b border-stone-200 px-4 py-3">
                <div className="flex items-center gap-2">
                  <Cpu className="h-3.5 w-3.5 text-stone-500" />
                  <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-700">
                    Agent trace
                  </span>
                  <span className="text-[11px] text-stone-400">
                    {actions.length === 0 ? "no actions yet" : `${actions.length} actions`}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <EvalsPill onOpen={() => setEvalsOpen(true)} />
                  <button
                    type="button"
                    onClick={() => setTruthOpen(true)}
                    aria-label="What is JoshLM-7B really?"
                    title="What is JoshLM-7B really?"
                    className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-stone-200 text-stone-500 transition hover:border-stone-900 hover:text-stone-900"
                  >
                    <HelpCircle className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              {actions.length === 0 ? (
                <div className="px-4 py-4 text-[12px] text-stone-500">
                  Ask anything. Each query triggers <code className="rounded bg-stone-100 px-1">bm25Search</code>, optionally{" "}
                  <code className="rounded bg-stone-100 px-1">embeddingRerank</code>, then{" "}
                  <code className="rounded bg-stone-100 px-1">composeResponse</code>. Full timings and scores show here.
                </div>
              ) : (
                <div className="px-3 py-3">
                  <TraceScrubber
                    actions={actions}
                    renderCard={(a) => <ActionCard action={a} />}
                  />
                </div>
              )}

              <div className="border-t border-stone-200 px-4 py-2.5 text-[10.5px] leading-relaxed text-stone-500">
                <p>
                  JoshLM-7B · <span className="italic">fine-tuned on a life of code</span> ·
                  {" "}p95 &lt; 400ms · 22 MB
                </p>
                <p className="mt-0.5 text-stone-400">
                  <button onClick={() => setTruthOpen(true)} className="underline decoration-stone-300 hover:text-stone-900">
                    What's actually under the hood?
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── input ──────────────────────────────────────────── */}
        {/*
            Positioned to clear the trace panel on the right (360px + gap).
            On mobile it falls back to full-width fixed bottom.
        */}
        <div className="fixed bottom-6 left-0 right-0 px-6 lg:right-[392px]">
          {/* Smart Mode bar + Pro Mode toggle + TTS toggle */}
          {capability && capability.decision !== "unsupported" && (
            <div className="mx-auto mb-2 flex max-w-3xl items-center gap-2 rounded-xl border border-stone-200 bg-white/85 px-3 py-1.5 text-[11.5px] shadow-[0_2px_10px_rgba(0,0,0,0.04)] backdrop-blur">
              <div className="min-w-0 flex-1">
                <SmartModeIndicator
                  smartMode={smartMode}
                  onToggle={() => setSmartMode((s) => !s)}
                  llmState={llm.state}
                  capability={capability}
                  onCancel={llm.cancel}
                />
              </div>
              <ProModeToggle proMode={proMode} onToggle={() => setProMode((p) => !p)} />
              {tts.supported && (
                <button
                  type="button"
                  onClick={tts.toggle}
                  className={`inline-flex shrink-0 items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] transition ${
                    tts.enabled
                      ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                      : "border-stone-300 text-stone-600 hover:border-stone-900 hover:text-stone-900"
                  }`}
                  title={
                    tts.enabled
                      ? `Voice on (${tts.voiceName ?? "browser default"})`
                      : "Read replies aloud"
                  }
                >
                  {tts.enabled ? (
                    <Volume2 className="h-3 w-3" />
                  ) : (
                    <VolumeX className="h-3 w-3" />
                  )}
                  <span>{tts.enabled ? "Voice" : "Voice"}</span>
                  {tts.state === "speaking" && (
                    <span className="ml-0.5 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
                  )}
                </button>
              )}
            </div>
          )}
          <form
            onSubmit={(e) => { e.preventDefault(); send(input); }}
            className="mx-auto flex max-w-3xl items-end gap-2 rounded-2xl border border-stone-200 bg-white p-2 shadow-[0_8px_30px_rgba(0,0,0,0.06)]"
          >
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send(input);
                }
              }}
              rows={1}
              placeholder={voice.state === "listening" ? "Listening…" : "Ask anything (and watch the trace)…"}
              className="min-h-[36px] flex-1 resize-none bg-transparent px-3 py-2 text-[14.5px] text-stone-900 placeholder-stone-400 outline-none"
            />
            {voice.state !== "unsupported" && (
              <button
                type="button"
                onClick={voice.toggle}
                disabled={streaming}
                className={`inline-flex h-9 w-9 items-center justify-center rounded-xl transition disabled:opacity-30 ${
                  voice.state === "listening"
                    ? "bg-rose-500 text-stone-50 hover:bg-rose-600"
                    : "border border-stone-300 text-stone-600 hover:border-stone-900 hover:text-stone-900"
                }`}
                aria-label="Voice input"
              >
                {voice.state === "listening" ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              </button>
            )}
            <button
              type="submit"
              disabled={!input.trim() || streaming}
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-stone-900 text-stone-50 transition hover:bg-stone-700 disabled:opacity-30"
              aria-label="Send"
            >
              <ArrowUp className="h-4 w-4" />
            </button>
          </form>
          <p className="mx-auto mt-2 max-w-3xl text-center text-[11px] text-stone-400">
            JoshLM-7B may be{" "}
            <button onClick={() => setTruthOpen(true)} className="underline decoration-stone-300 hover:text-stone-900">
              less of a model than advertised
            </button>
            . For the source of truth, see the <a href="/resume-openai-startups.pdf" className="underline">resume</a> or <a href="mailto:harsh.joshi.pth@gmail.com" className="underline">email Harsh</a>.
          </p>
        </div>
      </div>

      {/* ── Truth modal ───────────────────────────────────────── */}
      <AnimatePresence>
        {truthOpen && (
          <TruthModal
            onClose={() => setTruthOpen(false)}
            smartModeOn={smartMode && llm.state.status === "ready"}
            proModeOn={proMode}
          />
        )}
      </AnimatePresence>

      {/* ── Eval suite sheet ──────────────────────────────────── */}
      <EvalsSheet open={evalsOpen} onClose={() => setEvalsOpen(false)} />
    </div>
  );
}

// ───────────────────────── small components ─────────────────────────

// Parse WebLLM's verbose status text into structured fields.
// Examples we see:
//   "Fetching param cache[14/30]: 438MB fetched. 52% completed, 17 secs elapsed. ..."
//   "Loading model from cache[3/30]"
//   "Finish loading on WebGPU"
type ParsedProgress = {
  stage: "fetching" | "loading-cache" | "compiling" | "finalising";
  shardDone: number | null;
  shardTotal: number | null;
  mbFetched: number | null;
  elapsedSec: number | null;
};
function parseProgress(text: string): ParsedProgress {
  let stage: ParsedProgress["stage"] = "fetching";
  if (/loading model from cache/i.test(text)) stage = "loading-cache";
  else if (/(compil|wasm|webgpu)/i.test(text)) stage = "compiling";
  else if (/finish/i.test(text)) stage = "finalising";

  const shardMatch = text.match(/\[(\d+)\/(\d+)\]/);
  const mbMatch = text.match(/(\d+(?:\.\d+)?)\s*MB\s+fetched/i);
  const secMatch = text.match(/(\d+(?:\.\d+)?)\s*secs?\s+elapsed/i);

  return {
    stage,
    shardDone: shardMatch ? Number(shardMatch[1]) : null,
    shardTotal: shardMatch ? Number(shardMatch[2]) : null,
    mbFetched: mbMatch ? Number(mbMatch[1]) : null,
    elapsedSec: secMatch ? Number(secMatch[1]) : null,
  };
}

function stageLabel(stage: ParsedProgress["stage"]): string {
  switch (stage) {
    case "loading-cache": return "Loading from cache";
    case "compiling": return "Compiling for your GPU";
    case "finalising": return "Almost ready";
    default: return "Downloading model";
  }
}

function etaSec(progress: number, elapsedSec: number | null): number | null {
  if (!elapsedSec || progress <= 0.01 || progress >= 0.98) return null;
  const total = elapsedSec / progress;
  return Math.max(1, Math.round(total - elapsedSec));
}

function formatEta(sec: number): string {
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

// Grounding guardrails moved to src/lib/groundedness.ts — topic-agnostic
// retrieval gate + semantic + entity checks. See that file for rationale.

// SmartMode indicator: toggle, status, progress bar, cancel
// ProModeToggle — switch /ai to the Vercel AI SDK + Groq Llama 3.3 70B
// path. Distinct from Smart Mode (which loads Qwen-1.5B in the browser):
// Pro Mode hits a real server harness with native function calling.
function ProModeToggle({
  proMode,
  onToggle,
}: {
  proMode: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`inline-flex shrink-0 items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] transition ${
        proMode
          ? "border-violet-300 bg-violet-50 text-violet-700"
          : "border-stone-300 text-stone-600 hover:border-stone-900 hover:text-stone-900"
      }`}
      title={
        proMode
          ? "Pro Mode ON — Groq Llama 3.3 70B + Vercel AI SDK tool calling"
          : "Pro Mode OFF — switch to real 70B model via Groq"
      }
    >
      <Rocket className="h-3 w-3" />
      <span>Pro</span>
      {proMode && (
        <span className="ml-0.5 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-violet-500" />
      )}
    </button>
  );
}

function SmartModeIndicator({
  smartMode,
  onToggle,
  llmState,
  capability,
  onCancel,
}: {
  smartMode: boolean;
  onToggle: () => void;
  llmState: ReturnType<typeof useGenerativeLLM>["state"];
  capability: CapabilityReport;
  onCancel: () => void;
}) {
  const status = llmState.status;

  if (!smartMode) {
    return (
      <div className="flex w-full items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-stone-600">
          <Zap className="h-3 w-3 text-stone-400" />
          <span>
            Smart mode is <strong className="text-stone-900">off</strong> · using BM25 + templates
          </span>
        </div>
        <button
          type="button"
          onClick={onToggle}
          className="shrink-0 rounded-md border border-stone-300 px-2 py-0.5 text-[11px] text-stone-700 transition hover:border-stone-900 hover:text-stone-900"
          title={
            capability.decision === "opt-in"
              ? `Will download ~1 GB. Reasons: ${capability.reasons.join(", ")}`
              : "Loads Qwen-1.5B (~1 GB, cached after first download)"
          }
        >
          Turn on
        </button>
      </div>
    );
  }

  if (status === "loading") {
    const pct = Math.round((llmState as { progress: number }).progress * 100);
    const raw = (llmState as { text: string }).text;
    const parsed = parseProgress(raw);
    const eta = etaSec((llmState as { progress: number }).progress, parsed.elapsedSec);

    // Build the right-side detail string from parsed fields only
    const bits: string[] = [];
    if (parsed.shardDone !== null && parsed.shardTotal !== null) {
      bits.push(`shard ${parsed.shardDone} of ${parsed.shardTotal}`);
    }
    if (parsed.mbFetched !== null) bits.push(`${parsed.mbFetched} MB`);
    if (eta !== null) bits.push(`~${formatEta(eta)} left`);
    const detail = bits.join(" · ");

    return (
      <div className="flex w-full flex-col gap-1.5">
        {/* row 1: label + percent + cancel */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-stone-700">
            <Zap className="h-3 w-3 animate-pulse text-amber-500" />
            <span>{stageLabel(parsed.stage)}</span>
            <span className="font-mono tabular-nums text-stone-500">{pct}%</span>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="shrink-0 rounded-md border border-stone-300 px-2 py-0.5 text-[10.5px] text-stone-500 transition hover:border-rose-500 hover:text-rose-500"
          >
            Cancel
          </button>
        </div>
        {/* row 2: full-width progress bar */}
        <div className="h-1 w-full overflow-hidden rounded-full bg-stone-200">
          <div
            className="h-full bg-gradient-to-r from-amber-400 to-amber-500 transition-all duration-300"
            style={{ width: `${pct}%` }}
          />
        </div>
        {/* row 3: structured detail, no raw text dump */}
        <div className="flex items-center justify-between text-[10.5px] text-stone-400">
          <span className="tabular-nums">{detail || "starting…"}</span>
          <span className="italic">first load only · cached forever after</span>
        </div>
      </div>
    );
  }

  if (status === "ready") {
    return (
      <div className="flex w-full items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-stone-700">
          <Zap className="h-3 w-3 text-emerald-500" />
          <span>
            Smart mode is <strong className="text-stone-900">on</strong> · Qwen-1.5B ready
            {(llmState as { warmed: boolean }).warmed ? (
              <span className="ml-1 text-stone-400">(cached)</span>
            ) : null}
          </span>
        </div>
        <button
          type="button"
          onClick={onToggle}
          className="shrink-0 rounded-md border border-stone-300 px-2 py-0.5 text-[11px] text-stone-700 transition hover:border-stone-900 hover:text-stone-900"
        >
          Turn off
        </button>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="flex w-full items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-rose-600">
          <Zap className="h-3 w-3" />
          <span>Smart mode failed: {(llmState as { error: string }).error.slice(0, 80)}</span>
        </div>
        <button
          type="button"
          onClick={onToggle}
          className="shrink-0 rounded-md border border-stone-300 px-2 py-0.5 text-[11px] text-stone-700 transition hover:border-stone-900 hover:text-stone-900"
        >
          Turn off
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 text-stone-500">
      <Zap className="h-3 w-3" />
      <span>Preparing smart mode…</span>
    </div>
  );
}

function JoshLMBadge() {
  return (
    <div className="relative inline-block" style={{ width: 44, height: 44 }}>
      <div className="absolute inset-0 rounded-xl bg-stone-900" />
      <div className="absolute inset-0 flex items-center justify-center text-[9.5px] font-bold tracking-tight text-stone-50">
        Josh<br />LM
      </div>
    </div>
  );
}

// Parse [chunk-id] markers in a response into renderable nodes —
// text segments + interactive citation badges that show the source chunk
// text on hover. Citations are the proof that the agent's claims came
// from real corpus content; rendering them inline closes the trust loop.
function renderWithCitations(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const re = /\[([a-z0-9][\w-]{1,40})\]/gi;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const id = m[1];
    const chunk = FULL_CORPUS.find((c) => c.id === id);
    if (chunk) {
      parts.push(
        <CitationBadge key={`c${key++}`} id={id} sourceText={chunk.text} href={chunk.href} />,
      );
    } else {
      // Unknown id — render verbatim so it's visible something went off
      parts.push(<span key={`u${key++}`} className="text-rose-500">[{id}]</span>);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function CitationBadge({
  id,
  sourceText,
  href,
}: {
  id: string;
  sourceText: string;
  href?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <span
      className="relative mx-0.5 inline-block align-baseline"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      tabIndex={0}
    >
      <a
        href={href ?? `#chunk-${id}`}
        target={href ? "_blank" : undefined}
        rel="noreferrer"
        className="inline-flex items-center rounded-[3px] border border-stone-300 bg-stone-50 px-1 py-px text-[10px] font-mono text-stone-600 transition hover:border-stone-900 hover:text-stone-900"
      >
        {id}
      </a>
      {open && (
        <span className="absolute left-0 top-full z-10 mt-1 inline-block w-72 rounded-md border border-stone-200 bg-white p-2 text-[12px] leading-relaxed text-stone-700 shadow-lg">
          <span className="block font-mono text-[10px] uppercase tracking-wider text-stone-400">
            {id}
          </span>
          <span className="mt-0.5 block">{sourceText}</span>
        </span>
      )}
    </span>
  );
}

function MessageRow({
  message,
  streaming = false,
  trust,
}: {
  message: Message;
  streaming?: boolean;
  trust?: TrustVerdict;
}) {
  const isUser = message.role === "user";
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="flex gap-3">
      {isUser ? (
        <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-stone-900 text-[11px] font-semibold text-stone-50">
          You
        </div>
      ) : (
        <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-stone-900 text-[7.5px] font-bold leading-tight text-stone-50">
          Josh<br/>LM
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-medium uppercase tracking-wider text-stone-400">
          {isUser ? "You" : "JoshLM-7B"}
        </div>
        <div className={`mt-1 text-[14.5px] leading-[1.6] ${isUser ? "text-stone-700" : "text-stone-800"}`}>
          {isUser ? message.content : renderWithCitations(message.content)}
          {streaming && (
            <span className="ml-0.5 inline-block h-3.5 w-1 translate-y-[2px] animate-pulse bg-stone-400" />
          )}
        </div>
        {!isUser && trust && <TrustBadge trust={trust} />}
      </div>
    </motion.div>
  );
}

// TrustBadge — one-line groundedness verdict + click-to-expand per-sentence
// audit. Surfaces under every Pro Mode answer so visitors can verify each
// claim against its source chunk without opening the trace panel.
function TrustBadge({ trust }: { trust: TrustVerdict }) {
  const [open, setOpen] = useState(false);
  const tone =
    trust.overall === "grounded"
      ? "text-emerald-700 border-emerald-200 bg-emerald-50"
      : trust.overall === "partial"
        ? "text-amber-700 border-amber-200 bg-amber-50"
        : "text-rose-700 border-rose-200 bg-rose-50";
  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[10.5px] ${tone}`}
        title="Open per-sentence groundedness audit"
      >
        <span className="font-semibold uppercase tracking-wider">{trust.overall}</span>
        <span className="opacity-70">·</span>
        <span>
          {trust.groundedSentences}/{trust.totalSentences} grounded
        </span>
        <span className="opacity-70">·</span>
        <span>
          {trust.toolCalls} tool{trust.toolCalls === 1 ? "" : "s"}
        </span>
        <span className="opacity-70">·</span>
        <span>{trust.unsupportedEntities.length} unsupported</span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-2 space-y-1 rounded-md border border-stone-200 bg-white p-2 text-[11.5px]">
              {trust.perSentence.map((s, i) => (
                <div key={i} className="flex items-start gap-1.5">
                  <span
                    className={`mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full ${
                      s.status === "grounded"
                        ? "bg-emerald-500"
                        : s.status === "partial"
                          ? "bg-amber-500"
                          : "bg-rose-500"
                    }`}
                  />
                  <span className="flex-1 text-stone-700">{s.sentence}</span>
                  <span className="shrink-0 font-mono text-[10px] tabular-nums text-stone-400">
                    {s.maxCosine.toFixed(2)}
                  </span>
                  {s.supportedBy && (
                    <span className="shrink-0 font-mono text-[10px] text-stone-500">
                      [{s.supportedBy}]
                    </span>
                  )}
                </div>
              ))}
              {trust.unsupportedEntities.length > 0 && (
                <p className="border-t border-stone-100 pt-1.5 text-[10.5px] text-stone-500">
                  Unsupported entities:{" "}
                  <span className="font-mono">{trust.unsupportedEntities.join(", ")}</span>
                </p>
              )}
              <p className="border-t border-stone-100 pt-1.5 text-[10px] text-stone-400">
                Mechanical check — embedding cosine + entity match. No LLM judge in v1.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ActionCard({ action }: { action: AgentAction }) {
  const [expanded, setExpanded] = useState(false);
  const status = action.status;
  const detail = action.detail;

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-lg border border-stone-200 bg-white px-3 py-2.5"
    >
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-start gap-2 text-left"
      >
        <span className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
        <div className="min-w-0 flex-1">
          <code className="text-[12.5px] font-medium text-stone-900">{action.name}</code>
          {detail && status === "ok" && (
            <p className="mt-0.5 line-clamp-1 text-[11.5px] text-stone-600">
              {summariseDetail(action.name, detail)}
            </p>
          )}
        </div>
        <span className="ml-2 mt-1 shrink-0 text-[10px] tabular-nums text-stone-400">
          {status === "running" ? "…" : action.durationMs !== undefined ? `${action.durationMs}ms` : ""}
        </span>
      </button>
      <AnimatePresence>
        {expanded && detail && (
          <motion.pre
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="mt-2 max-h-64 overflow-auto rounded bg-stone-900 px-3 py-2 font-mono text-[10.5px] leading-relaxed text-stone-100"
          >
            {JSON.stringify(detail, null, 2)}
          </motion.pre>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function summariseDetail(name: AgentAction["name"], detail: Record<string, unknown>): string {
  if (name === "bm25Search") {
    const hits = detail.topHits as Array<{ id: string; score: number }> | undefined;
    if (!hits || hits.length === 0) return "0 hits";
    return `${hits.length} hits · top: ${hits[0].id} (${hits[0].score})`;
  }
  if (name === "embeddingRerank") {
    const hits = detail.topHits as Array<{ id: string; score: number }> | undefined;
    if (!hits || hits.length === 0) return "0 hits";
    return `${hits.length} hits · top: ${hits[0].id} (${hits[0].score.toFixed(3)})`;
  }
  if (name === "composeResponse") {
    const intent = detail.intent as string;
    const cited = (detail.citedChunkIds as string[] | undefined) ?? [];
    return `intent=${intent} · cited ${cited.length} chunk${cited.length === 1 ? "" : "s"}`;
  }
  if (name === "generateResponse") {
    const model = detail.model as string | undefined;
    const ftms = detail.firstTokenMs as number | null | undefined;
    const tokens = detail.tokensGenerated as number | undefined;
    return `${model?.split("-").slice(0, 3).join("-")} · ${tokens ?? 0} tokens · first ${ftms ?? "?"}ms`;
  }
  if (name === "groundednessCheck") {
    const layer = detail.layer as string;
    if (layer === "retrieval-gate") {
      const passed = (detail as { passed: boolean }).passed;
      const bm25 = (detail as { topBM25: number }).topBM25;
      const cos = (detail as { topEmbeddingCosine: number }).topEmbeddingCosine;
      return passed
        ? `gate passed · bm25=${bm25.toFixed(2)} · cos=${cos.toFixed(2)}`
        : `gate FAILED · bm25=${bm25.toFixed(2)} · cos=${cos.toFixed(2)} (off-corpus)`;
    }
    const overall = detail.overall as string;
    const weak = detail.semanticWeakestScore as number | undefined;
    const ent = detail.entityMatchRate as number | undefined;
    const unsup = (detail.entityUnsupported as string[] | undefined) ?? [];
    if (overall === "passed") {
      return `grounded · sem≥${weak?.toFixed(2) ?? "?"} · ent=${ent ?? "?"}`;
    }
    if (overall === "failed-semantic") {
      return `UNGROUNDED claim · sem=${weak?.toFixed(2)} → retrying`;
    }
    if (overall === "failed-entity") {
      return `unsupported entities: ${unsup.slice(0, 2).join(", ")} → retrying`;
    }
    return `${overall}`;
  }
  if (name === "mcpToolCall") {
    const tool = detail.tool as string;
    const args = detail.args as Record<string, unknown> | undefined;
    const error = detail.error as string | undefined;
    if (error) return `${tool}() → MCP error: ${error.slice(0, 60)}`;
    const argStr = args
      ? Object.entries(args)
          .map(([k, v]) => `${k}: ${typeof v === "string" ? `"${v}"` : String(v)}`)
          .join(", ")
      : "";
    return `tools/call ${tool}(${argStr})`;
  }
  if (name === "reflectionRetry") {
    const reason = detail.reason as string;
    const outcome = detail.retryOutcome as string | undefined;
    if (!outcome) return `retrying after ${reason}…`;
    return outcome === "passed"
      ? `retry succeeded · grounded after correction`
      : `retry still ungrounded → falling back to template`;
  }
  if (name === "agentThought") {
    const iter = (detail.iteration as number) ?? 0;
    const thought = (detail.thought as string) ?? "";
    return `step ${iter} · thinking: ${thought.slice(0, 70)}${thought.length > 70 ? "…" : ""}`;
  }
  if (name === "agentAction") {
    const iter = (detail.iteration as number) ?? 0;
    const summary = (detail.summary as string) ?? (detail.toolName as string) ?? "action";
    return `step ${iter} · ${summary}`;
  }
  if (name === "agentObservation") {
    const iter = (detail.iteration as number) ?? 0;
    const obs = (detail.observation as string) ?? "(no summary)";
    return `step ${iter} · observed: ${obs.slice(0, 70)}${obs.length > 70 ? "…" : ""}`;
  }
  return "ok";
}

// Render a tool-call output (Pro Mode) as a one-line observation string
// for the trace card. Unknown shapes get a JSON stringification fallback.
function summarisePoolOutput(output: unknown): string {
  if (output == null) return "no output";
  if (typeof output !== "object") return String(output);
  const o = output as Record<string, unknown>;
  if (typeof o.error === "string") return `error: ${o.error}`;
  // Common shapes from our 7 tools — try to surface the headline number/string
  if (typeof o.fullName === "string" && typeof o.stars === "number")
    return `${o.fullName} · ${o.stars}★`;
  if (typeof o.name === "string" && typeof o.latestVersion === "string")
    return `${o.name} v${o.latestVersion}${typeof o.weeklyDownloads === "number" ? ` · ${o.weeklyDownloads}/wk` : ""}`;
  if (typeof o.matchCount === "number") return `${o.matchCount} roles matched`;
  if (Array.isArray((o as { posts?: unknown[] }).posts))
    return `${(o.posts as unknown[]).length} posts`;
  if (typeof o.summary === "string") return o.summary;
  try {
    return JSON.stringify(output).slice(0, 100);
  } catch {
    return "object";
  }
}

function TruthModal({
  onClose,
  smartModeOn,
  proModeOn,
}: {
  onClose: () => void;
  smartModeOn: boolean;
  proModeOn: boolean;
}) {
  // Headline reveal escalates with whichever mode is currently active.
  // Default: the "7B" is BM25 + templates. Smart Mode: there's actually
  // a real 1.5B in your browser. Pro Mode: surprise, the 7B sandbagging
  // was real — a 70B is on the other end of /api/agent. The joke holds
  // at each layer because each reveal is honest about what's underneath.
  const headline = proModeOn
    ? "JoshLM-7B was sandbagging. You flipped Pro Mode — that's Llama 3.3 70B on Groq's LPU. The \"7B\" was off by an order of magnitude in the other direction."
    : smartModeOn
      ? "JoshLM-7B isn't 7B. It's Qwen2.5-1.5B, running in your browser, not a datacenter. The marketing was only slightly off."
      : "JoshLM-7B isn't a 7-billion-parameter model. It's not a model at all in the way most people mean.";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/40 px-4 py-6 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="relative max-h-[88vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-8 shadow-2xl"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 inline-flex h-7 w-7 items-center justify-center rounded-full text-stone-400 transition hover:bg-stone-100 hover:text-stone-900"
        >
          <X className="h-4 w-4" />
        </button>

        <h3 className="font-lora text-[26px] leading-tight text-stone-900">OK, real talk.</h3>
        <p className="font-lora mt-3 text-[15.5px] italic leading-relaxed text-stone-600">
          {headline}
        </p>

        <div className="mt-6 space-y-4 text-[14px] leading-relaxed text-stone-700">
          <p>What's actually running, layer by layer:</p>
          <ul className="ml-1 space-y-2 border-l-2 border-stone-200 pl-4">
            <li>
              <strong className="text-stone-900">BM25</strong> — Robertson-Sparck Jones probabilistic
              retrieval, k1=1.5, b=0.75. Hand-implemented, ~120 lines of TypeScript, zero
              dependencies. Indexes a curated corpus of ~200 chunks (hand-written +
              auto-generated from his GitHub).
            </li>
            <li>
              <strong className="text-stone-900">all-MiniLM-L6-v2</strong> — a 22 MB
              sentence-transformer embedding model running in your browser via{" "}
              <a
                href="https://huggingface.co/docs/transformers.js"
                target="_blank"
                rel="noreferrer"
                className="underline decoration-stone-400 hover:text-stone-900"
              >
                @huggingface/transformers
              </a>
              . 384-dim embeddings, cosine similarity rerank. ~80 ms per query after first load.
            </li>
            <li>
              <strong className="text-stone-900">Reciprocal Rank Fusion</strong> (k=60) — merges
              BM25 and semantic rankings without needing score calibration. Standard IR fusion
              technique (Cormack et al. 2009).
            </li>
            <li>
              <strong className="text-stone-900">Rule-based intent classifier + template composer</strong>{" "}
              — picks one of ~13 intent classes, then stitches retrieved chunks into a natural 2-4
              sentence reply using randomised connective banks. Zero generation in this default
              path.
            </li>
            <li>
              <strong className="text-stone-900">3-layer groundedness gate</strong> — retrieval
              confidence (BM25 + embedding-cosine thresholds), per-sentence semantic grounding,
              entity grounding. Off-corpus questions refuse <em>before</em> any model is invoked.
              Topic-agnostic, no keyword denylists.
            </li>
            <li>
              <strong className="text-stone-900">7 MCP-style tools</strong> for live data —{" "}
              GitHub, npm, PyPI, App Store, OpenAI's Ashby job board, Substack RSS, resume sections.
              Same Zod schemas across browser & server; native JSON-RPC envelope on the browser
              path, AI-SDK <code className="rounded bg-stone-100 px-1">tool()</code> on the server.
            </li>
            {smartModeOn && (
              <li>
                <strong className="text-stone-900">Qwen2.5-1.5B-Instruct</strong> (Smart Mode) — a
                real 1.5 B language model, 4-bit quantised (~1 GB), running on your GPU via WebGPU
                through{" "}
                <a
                  href="https://webllm.mlc.ai"
                  target="_blank"
                  rel="noreferrer"
                  className="underline decoration-stone-400 hover:text-stone-900"
                >
                  WebLLM
                </a>
                . Drives a hand-rolled ReAct loop (Thought / Action / Observation) with a
                defensive parser, since 1.5 B is too small for reliable native function calling.
                Inference happens entirely in your browser; nothing leaves the tab.
              </li>
            )}
            {proModeOn && (
              <li>
                <strong className="text-stone-900">Llama 3.3 70B on Groq</strong> (Pro Mode){" "}
                <span className="rounded bg-violet-100 px-1 text-[11px] font-medium uppercase tracking-wider text-violet-700">
                  new
                </span>{" "}
                — when you flip the rocket toggle, requests leave your browser and hit a Vercel
                Edge function (
                <code className="rounded bg-stone-100 px-1">api/agent.ts</code>) running the{" "}
                <a
                  href="https://ai-sdk.dev/"
                  target="_blank"
                  rel="noreferrer"
                  className="underline decoration-stone-400 hover:text-stone-900"
                >
                  Vercel AI SDK
                </a>{" "}
                with the{" "}
                <a
                  href="https://ai-sdk.dev/providers/ai-sdk-providers/groq"
                  target="_blank"
                  rel="noreferrer"
                  className="underline decoration-stone-400 hover:text-stone-900"
                >
                  Groq provider
                </a>
                . Native function calling — no regex parser. <code className="rounded bg-stone-100 px-1">stopWhen: stepCountIs(4)</code>. UI message stream parses straight into the existing trace cards. Free tier: 1 000 req/day on Llama 3.3 70B; we fall back to JoshLM-7B silently on 429.
              </li>
            )}
          </ul>

          <p className="pt-1">New in this build:</p>
          <ul className="ml-1 space-y-2 border-l-2 border-stone-200 pl-4">
            <li>
              <strong className="text-stone-900">Public eval suite</strong> — 12 mechanical cases
              (5 golden, 4 refusal, 3 adversarial). Click the{" "}
              <span className="inline-flex items-center gap-1 rounded border border-stone-200 bg-stone-50 px-1 py-0.5 font-mono text-[10px]">
                ● evals
              </span>{" "}
              pill in the trace header to grade live. No LLM judge — substring + retrieved-id
              checks only, so the numbers are reproducible. A few cases honestly fail today and the
              UI shows you which.
            </li>
            <li>
              <strong className="text-stone-900">Pro Mode trust audit</strong> — after every Pro
              Mode answer, a per-sentence groundedness check runs in your browser (semantic
              cosine + entity grounding, same checker the browser path uses). Surfaces as a
              one-line badge under each response — click to see each sentence highlighted by its
              source chunk, with raw cosine scores.
            </li>
            <li>
              <strong className="text-stone-900">Scrubable trace replay</strong> — the right
              panel's stack of action cards is now a horizontal timeline. Drag through every step
              of retrieval → compose → groundedness → tool calls. Arrow keys to scrub, End for the
              live tail. Auto-falls back to vertical stack on mobile.
            </li>
          </ul>

          <div className="rounded-lg bg-stone-50 p-4 text-[13px] leading-relaxed">
            <p className="mb-1">
              <strong className="text-stone-900">Default path:</strong> ~22 MB added (model cached
              after first load), ~30 MB RAM, &lt;400 ms p95, <strong>$0/query</strong>.
            </p>
            {smartModeOn && (
              <p className="mb-1">
                <strong className="text-stone-900">+ Smart Mode:</strong> +1 GB (Qwen, cached
                after first load), runs on your GPU, still <strong>$0/query</strong>.
              </p>
            )}
            {proModeOn && (
              <p>
                <strong className="text-stone-900">+ Pro Mode:</strong> 0 MB client overhead, ~1-2 s
                first token on Groq's LPU, falls back gracefully on rate limit.{" "}
                <strong>~$0/query</strong> under free tier (1k req/day).
              </p>
            )}
          </div>

          <p>
            Why ship it like this? Most "AI" products in production are 80% retrieval + 20% LLM,
            with 100% marketing claims. This is a portfolio version of that pattern, played for
            the joke — but the engineering is textbook and the eval discipline is real.
          </p>

          <p className="italic text-stone-500">
            Source visible at{" "}
            <a
              href="https://github.com/josharsh"
              target="_blank"
              rel="noreferrer"
              className="underline decoration-stone-400 hover:text-stone-900"
            >
              github.com/josharsh
            </a>
            . The BM25 implementation, the corpus, the composer, the agent loop, the eval suite,
            the Edge function — all there.
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ───────────────────────── helpers ─────────────────────────

// Fake streaming with deliberately uniform cadence. Real LLM streams have
// variable speed because tokens arrive from inference; pure-script streams
// don't. Engineers spot the tell. This is by design.
async function fakeStream(
  text: string,
  onDelta: (delta: string, acc: string) => void,
  chunkSize = 3,
  delayMs = 22,
) {
  let acc = "";
  for (let i = 0; i < text.length; i += chunkSize) {
    const delta = text.slice(i, i + chunkSize);
    acc += delta;
    onDelta(delta, acc);
    await new Promise((r) => setTimeout(r, delayMs));
  }
}
